<template>
  <div>
    <k-panel title="Project Impact" :hasContentToggle="false">
      <template #body>
        <div class="project-impact-statement" :class="{ 'with-feedback': showFeedbackPanel }">
          <div class="impact-left">
            <div class="impact-description">
              <p>
                <strong>Turn your project into proof of your value.</strong>
                Every project represents a real contribution to your business. Capturing the tangible impact of your work
                – like hours saved, costs reduced, or processes improved – is the best way to prove your value to your
                manager and strengthen your professional track record.
              </p>
              <p>
                Draft your impact statement below, and let K.A.T.E. help you to polish it into a powerful, data-backed
                summary of your success.
              </p>
              <k-disclaimer type="info">
                <p>
                  <strong>Please note:</strong> Your submission may be reviewed by our team and shared directly with your
                  employer, as well as with our AI Assistant if you ask for its feedback. Please ensure that you do not
                  share anything confidential using this tool.
                </p>
              </k-disclaimer>
            </div>

            <div class="impact-textarea-wrapper">
              <textarea
                id="project-impact-statement-input"
                v-model="impactStatement"
                class="form-control"
                rows="6"
                placeholder="Describe the impact of your project..."
              ></textarea>
            </div>

            <div class="impact-options">
              <k-toggle
                v-model="includeEvidenceForAIFeedback"
                label="Include Evidence for AI feedback"
              />
              <p class="toggle-description">
                This shares your uploaded files with Google Gemini to provide specific insights. Data is private to your account, and never used to train AI models.
                <a href="#"
                  class="privacy-info-link"
                  @click.prevent="showPrivacyInfo = !showPrivacyInfo">
                  Learn more<i :class="privacyChevronClass"></i>
                </a>
              </p>
              <transition name="privacy-expand">
              <div v-show="showPrivacyInfo" class="privacy-info">
                <h5 class="privacy-title">
                  <i class="far fa-shield" aria-hidden="true"></i> PRIVACY & DATA SECURITY
                </h5>
                <ul class="privacy-list">
                  <li>
                    <i class="fas fa-lock-alt" aria-hidden="true"></i>
                    <span><strong>Protection:</strong> Your data is covered by our existing Data Processing Agreement (DPA) and Privacy Policy.</span>
                  </li>
                  <li>
                    <i class="fas fa-eye" aria-hidden="true"></i>
                    <span><strong>Confidentiality:</strong> Files are processed by Google to generate feedback and are not stored or used to improve Google's public AI.</span>
                  </li>
                  <li>
                    <i class="fas fa-info-circle" aria-hidden="true"></i>
                    <span><strong>Sensitive Data:</strong> If your evidence contains highly privileged information not suitable for external processing, keep this toggled off. Your mentor will still see your files regardless.</span>
                  </li>
                </ul>
              </div>
              </transition>
            </div>

            <div class="impact-actions">
              <button
                type="button"
                class="btn btn-outlined feedback-panel-button"
                :disabled="askKateDisabled"
                @click="openFeedbackPanel"
              >
                <img :src="chatbotImage" class="chatbot-icon-small" alt="KATE bot avatar" aria-hidden="true" />
                Ask KATE <span v-if="includeEvidenceForAIFeedback">(with evidence)</span>
              </button>
              <button
                type="button"
                class="btn btn-success"
                :disabled="submitDisabled"
                @click="submitImpactStatement"
              >
                Submit Impact
              </button>
            </div>
            <k-disclaimer v-if="hasBeenSubmitted" type="success" class="impact-success-disclaimer">
              <p>
                <strong>Impact successfully logged!</strong>
                Great job capturing the business value of your work. Your polished statement is now ready to copy and
                share whenever you need to highlight your achievements – whether in a performance review, a manager 1:1,
                or a final assessment. You can come back to update your statement and tinker with it at any time.
              </p>
            </k-disclaimer>
          </div>
          <transition name="feedback-slide">
            <div v-if="showFeedbackPanel" class="impact-right">
              <div class="feedback-panel">
                <div class="feedback-panel-header">
                  <div class="explanation-header">
                    <img :src="chatbotImage" class="chatbot-icon" alt="KATE bot avatar"/>
                    <div class="title-section">
                      <h4 class="explanation-title">KATE's Feedback</h4>
                      <k-ai-generated-disclosure />
                    </div>
                  </div>
                  <button type="button" class="feedback-close-btn" @click="closeFeedbackPanel" aria-label="Close feedback">
                    <i class="fas fa-times" aria-hidden="true"></i>
                  </button>
                </div>
                <div class="explanation-container">
                  <div v-if="requestingFeedback" class="explanation-loading">
                    <i class="fas fa-spinner fa-spin" aria-hidden="true"></i> Generating feedback...
                  </div>
                  <div v-else-if="feedbackError" class="explanation-error">
                    <i class="fas fa-exclamation-circle" aria-hidden="true"></i> {{ feedbackError }}
                  </div>
                  <div v-else-if="aiFeedback" class="explanation-content" v-html="getMarkdown(aiFeedback)">
                  </div>
                  <div v-if="feedbackId && !requestingFeedback && !feedbackError" class="rating-container">
                    <button class="upvote"
                      @click="updateRating(true)"
                      :disabled="isRatingDisabled"
                      :class="rating === true ? 'upvoted' : ''"
                      aria-label="Upvote button"
                      title="Upvote button"
                      type="button">
                      <i class="fas fa-thumbs-up" aria-hidden="true"></i>
                    </button>
                    <button class="downvote"
                      @click="updateRating(false)"
                      :disabled="isRatingDisabled"
                      :class="rating === false ? 'downvoted' : ''"
                      aria-label="Downvote button"
                      title="Downvote button"
                      type="button">
                      <i class="fas fa-thumbs-down" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </template>
    </k-panel>
  </div>
</template>

<style scoped>
.project-impact-statement {
  padding: 15px 0;
  display: flex;
  gap: 1.5rem;
}

.impact-left {
  flex: 1;
  min-width: 0;
}

.project-impact-statement.with-feedback .impact-left {
  flex: 0 0 62%;
}

.impact-right {
  flex: 0 0 36%;
  min-width: 0;
}

.feedback-slide-enter-active,
.feedback-slide-leave-active {
  transition: all 0.3s ease;
}

.feedback-slide-enter-from,
.feedback-slide-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

.feedback-panel {
  position: sticky;
  top: 1rem;
  background-color: var(--kate-panel-alt);
  border: 1px solid var(--kate-border-primary, rgb(255 255 255 / 0.1));
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  max-height: 80vh;
}

.feedback-panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 12px;
}

.feedback-close-btn {
  background: none;
  border: none;
  color: var(--kate-type-primary, #6c757d);
  font-size: 1rem;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  line-height: 1;
}

.feedback-close-btn:hover {
  color: var(--kate-type-light, #e0e0e0);
  background-color: var(--kate-background-alt, rgb(255 255 255 / 0.05));
}

.impact-description {
  margin: 0 0 1rem;
  color: var(--kate-type-primary, #6c757d);
  font-size: 0.9rem;
  line-height: 1.5;
}

.impact-success-disclaimer {
  margin-top: 1.5rem;
}

.impact-textarea-wrapper {
  margin-bottom: 1rem;
}

.impact-textarea-wrapper textarea.form-control {
  resize: vertical;
  min-height: 120px;
}

.impact-options {
  margin-bottom: 1.25rem;
}

.toggle-description {
  margin: 0.25rem 0 0.75rem;
  padding-left: 0.25rem;
  color: var(--kate-type-primary, #6c757d);
  font-size: 0.78rem;
  line-height: 1.4;
}

.privacy-info-link {
  color: var(--kate-type-link);
  text-decoration: none;
  cursor: pointer;
}

.privacy-info-link:hover {
  text-decoration: underline;
}

.privacy-info-link i {
  margin-left: 5px;
  font-size: 0.8em;
}

.privacy-expand-enter-active,
.privacy-expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
  max-height: 300px;
  opacity: 1;
}

.privacy-expand-enter-from,
.privacy-expand-leave-to {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
}

.privacy-info {
  margin-top: 0.75rem;
  padding: 1rem 1.25rem;
  background-color: var(--kate-panel-alt, rgb(255 255 255 / 0.03));
  border: 1px solid var(--kate-border-primary, rgb(255 255 255 / 0.1));
  border-radius: 8px;
}

.privacy-title {
  margin: 0 0 0.75rem;
  font-size: 1rem;
  font-weight: bold;
  color: var(--kate-type-light, #e0e0e0);
  display: flex;
  align-items: center;
  gap: 8px;
}

.privacy-title i {
  color: var(--kate-primary, #5b8af0);
}

.privacy-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.privacy-list li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.85rem;
  line-height: 1.5;
  color: var(--kate-type-primary, #6c757d);
}

.privacy-list li i {
  flex-shrink: 0;
  width: 16px;
  text-align: center;
  margin-top: 3px;
  color: var(--kate-type-primary, #6c757d);
}

.privacy-list li strong {
  color: var(--kate-type-light, #e0e0e0);
}

.impact-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.feedback-panel-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.chatbot-icon-small {
  width: 20px;
  height: 20px;
}

.explanation-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.chatbot-icon {
  width: 32px;
  height: 32px;
}

.title-section {
  flex: 1;
}

.explanation-title {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.explanation-loading {
  color: var(--kate-type-secondary);
}

.explanation-error {
  color: var(--kate-error);
}

.explanation-content {
  line-height: 1.5;
}

.explanation-container {
  min-height: 100px;
  overflow-y: auto;
  background: var(--kate-chat);
  border: var(--kate-chat-border);
  border-radius: 12px;
  padding: 15px;
  flex: 1;
}

.rating-container {
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 10px;
  padding-right: 10px;
}

.rating-container button {
  background-color: var(--kate-background);
  border-radius: 50%;
  height: 35px;
  width: 35px;
  border: 1px solid;
  padding: 6px;
}

.rating-container button.upvote {
  color: var(--kate-success-alt);
  border-color: var(--kate-success-alt);
}

.rating-container button.upvote:hover,
.rating-container button.upvote.upvoted,
.rating-container button.upvote.upvoted:hover {
  color: var(--kate-type-success);
  border-color: var(--kate-type-success);
}

.rating-container button.downvote {
  color: var(--kate-danger-alt);
  border-color: var(--kate-danger-alt);
}

.rating-container button.downvote:hover,
.rating-container button.downvote.downvoted,
.rating-container button.downvote.downvoted:hover {
  color: var(--kate-danger);
  border-color: var(--kate-danger);
}

.rating-container button:disabled,
.rating-container button:disabled:hover {
  color: var(--kate-disabled);
  border-color: var(--kate-disabled);
  background-color: var(--kate-background);
}

@media (max-width: 768px) {
  .project-impact-statement {
    flex-direction: column;
  }

  .project-impact-statement.with-feedback .impact-left,
  .impact-right {
    flex: 1 1 100%;
  }
}
</style>

<script>
import KDisclaimer from '@base-components/k-disclaimer.vue';
import KPanel from '@base-components/k-panel.vue';
import KToggle from '@base-components/forms/k-toggle.vue';
import { importImage } from '@utils/dynamic-image-importer';
import { markdown } from '@utils/maths-markdown';
import KAiGeneratedDisclosure from '@base-components/ai/k-ai-generated-disclosure.vue';

export default {
  name: 'ProjectImpactStatement',
  components: {
    KDisclaimer,
    KPanel,
    KToggle,
    KAiGeneratedDisclosure,
  },

  props: {
    programmeId: {
      type: Number,
      required: true,
    },
    projectId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      impactStatement: '',
      includeEvidenceForAIFeedback: false,
      requestingFeedback: false,
      aiFeedback: '',
      feedbackError: null,
      showFeedbackPanel: false,
      feedbackId: null,
      rating: null,
      isRatingDisabled: false,
      submitting: false,
      hasBeenSubmitted: false,
      showPrivacyInfo: false,
    };
  },

  beforeMount() {
    this.getImpactStatement();
  },

  computed: {
    chatbotImage() {
      return importImage('icons/kate-bot-chat-colours.svg');
    },
    privacyChevronClass() {
      return this.showPrivacyInfo
        ? 'fas fa-angle-up'
        : 'fas fa-angle-down';
    },
    askKateDisabled() {
      return !this.impactStatement.trim() || this.requestingFeedback;
    },
    submitDisabled() {
      return !this.impactStatement.trim();
    },
  },

  methods: {
    getMarkdown(str) {
      return markdown(str);
    },

    openFeedbackPanel() {
      this.showFeedbackPanel = true;
      this.askKateForAIFeedback();
    },

    closeFeedbackPanel() {
      this.showFeedbackPanel = false;
    },

    askKateForAIFeedback() {
      this.requestingFeedback = true;
      this.hasBeenSubmitted = false; // Remove the submitted text from the UI
      const payload = {
        impact_statement: this.impactStatement.trim(),
        include_evidence: this.includeEvidenceForAIFeedback,
      };
      this.$logger.info('Requesting AI feedback for impact statement', { projectId: this.projectId });
      this.$http.post(`/api/chatbot/programmes/${this.programmeId}/projects/${this.projectId}/impact-statement/feedback`, payload)
        .then(response => {
          this.$logger.info('Successfully received AI feedback for impact statement', { projectId: this.projectId });
          this.aiFeedback = response.data.ai_feedback?.ai_feedback || response.data.feedback || '';
          this.feedbackId = response.data.ai_feedback?.id || null;
          this.rating = response.data.ai_feedback?.user_rating ?? null;
          this.isRatingDisabled = false;
        })
        .catch(err => {
          if (err.response?.status === 429) {
            this.$logger.autowarn('Rate limit exceeded for AI feedback', this.projectId);
            this.aiFeedback = 'KATE is receiving a lot of requests right now. Please try again later.';
            this.isRatingDisabled = true;
          } else {
            this.$logger.autowarn('Error requesting AI feedback for impact statement', this.projectId, err);
            this.feedbackError = err.response?.data?.error || 'An error occurred. Please try again later.';
          }
        })
        .then(() => {
          this.requestingFeedback = false;
        });
    },

    updateRating(value) {
      this.$logger.info('Updating impact statement AI feedback rating', { projectId: this.projectId });
      this.$http.put('/api/chatbot/projects/impact-statement/feedback/rating', {
        impact_statement_ai_feedback_id: this.feedbackId,
        user_rating: value,
      }).then(() => {
        this.$logger.info('Successfully updated AI feedback rating', { projectId: this.projectId });
        this.rating = value;
        this.isRatingDisabled = true;
      }).catch(err => {
        this.$logger.autowarn('Error updating AI feedback rating', this.projectId, err);
      });
    },

    getImpactStatement() {
      this.impactStatementReady = false;
      this.$logger.info('Getting project impact statement', this.projectId);
      this.$http.get(`/api/curriculum/programmes/projects/${this.projectId}/impact-statement`)
        .then(response => {
          this.$logger.info('Successfully got project impact statement', this.projectId);
          this.impactStatement = response.data?.impact_statement || '';
          this.includeEvidenceForAIFeedback = response.data?.latest_included_evidence || false;
          this.aiFeedback = response.data?.ai_feedback || '';
          this.hasBeenSubmitted = !!this.impactStatement && !this.aiFeedback;
          if (this.aiFeedback) {
            this.showFeedbackPanel = true;
          }
        })
        .catch(err => {
          this.$logger.autowarn('Error getting project impact statement', this.projectId, err);
          this.$ktoast.warning('Error getting project impact statement');
          this.impactStatement = '';
          this.includeEvidenceForAIFeedback = false;
          this.aiFeedback = '';
        }).then(() => {
          this.impactStatementReady = true;
        });
    },

    submitImpactStatement() {
      this.submitting = true;
      this.$logger.info('Submitting project impact statement', { projectId: this.projectId });
      this.$http.post(`/api/curriculum/programmes/projects/${this.projectId}/impact-statement`, { impact_statement: this.impactStatement.trim() })
        .then(() => {
          this.$logger.info('Successfully submitted project impact statement', { projectId: this.projectId });
          this.$ktoast.success('Project impact statement submitted');
          this.hasBeenSubmitted = true;
        })
        .catch(err => {
          this.$logger.autowarn('Error submitting project impact statement', this.projectId, err);
          this.$ktoast.warning('Error submitting project impact statement');
        })
        .then(() => {
          this.submitting = false;
        });
    },
  },
};
</script>
